import React, { useState, useMemo, useEffect } from 'react';
import { MaterialReactTable } from 'material-react-table';
import BillingFiltersHeader from '../Header/BillingFiltersHeader';
import BillingKPI from '../Header/BillingKPI';
import { FaFileInvoice } from 'react-icons/fa';
import { API_BASE_URL } from "helpers/urlHelper";
import { BillingUtils } from '../Services/billingService';
import { 
  TextField, 
  IconButton, 
  Box,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoIcon from '@mui/icons-material/Info';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonIcon from '@mui/icons-material/Person';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LinkIcon from '@mui/icons-material/Link';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

const BillingTable = ({ 
  activities = [], 
  isLoading,
  pagination,
  filters,
  selectedActivities = [],
  onFiltersChange,
  onPageChange,
  onActivitySelection,
  onMarkNonBillable,
  onUpdateActivity,
  currentUserRoles,
  onBulkReprice = () => {},
  onGenerateSalesOrders = () => {},
  onReloadData = () => {},
  currentCenterId = null,
  isProcessingBulkAction = false
}) => {
  
  // Local states
  const [processedActivities, setProcessedActivities] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [activeFilters, setActiveFilters] = useState({});
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  
  // Dialog states
  const [showNonBillableDialog, setShowNonBillableDialog] = useState(false);
  const [nonBillableActivityId, setNonBillableActivityId] = useState(null);
  const [nonBillableReason, setNonBillableReason] = useState('');
  const [nonBillableComment, setNonBillableComment] = useState('');
  
  // Vista detallada states
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedActivityForDetail, setSelectedActivityForDetail] = useState(null);

  const [forceEnrichTrigger, setForceEnrichTrigger] = useState(0);

  // Función auxiliar para obtener valor seguro con fallback
  const getSafeValue = (value, fallback = 'No especificado') => {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }
    return value;
  };

  // Función auxiliar para obtener nombre completo
  const getFullName = (person, fallback = 'No identificado') => {
    if (!person) return fallback;
    
    if (typeof person === 'string') {
      return person === 'unknown' ? fallback : `ID: ${person}`;
    }
    
    if (typeof person === 'object') {
      // Si tiene full_name, usarlo
      if (person.full_name) return person.full_name;
      
      // Si tiene name y surname, combinarlos
      if (person.name && person.surname) {
        return `${person.name} ${person.surname}`.trim();
      }
      
      // Si solo tiene name
      if (person.name) return person.name;
      
      // Si solo tiene _id
      if (person._id) return `ID: ${person._id}`;
    }
    
    return fallback;
  };

  // Función para formatear fecha
  const formatDate = (dateStr) => {
    if (!dateStr) return 'No especificada';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return 'Fecha inválida';
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch (error) {
      return 'Fecha inválida';
    }
  };

  // Función mejorada para obtener información del pagador principal
  const getPrimaryPayerInfo = (activity) => {
  if (!activity.split || !Array.isArray(activity.split) || activity.split.length === 0) {
    return { 
      name: 'Sin pagador asignado', 
      percentage: 0,
      amount: 0,
      type: 'unknown'
    };
  }
  
  // Buscar el pagador con mayor porcentaje o el primero si no hay porcentajes
  const sortedSplits = [...activity.split].sort((a, b) => (b.percent || 0) - (a.percent || 0));
  const primarySplit = sortedSplits[0];
  
  if (!primarySplit) {
    return { 
      name: 'Sin pagador asignado', 
      percentage: 0,
      amount: 0,
      type: 'unknown'
    };
  }
  
  let payerName = 'Pagador no identificado';
  let payerType = primarySplit.payer_type || 'unknown';
  
  if (primarySplit.payer_snapshot && primarySplit.payer_snapshot.name) {
    payerName = primarySplit.payer_snapshot.name;
  } else if (primarySplit.payer_id) {
    if (typeof primarySplit.payer_id === 'object' && primarySplit.payer_id !== null) {
      payerName = getFullName(primarySplit.payer_id, 'Pagador no identificado');
    } else {
      payerName = `Pagador ID: ${primarySplit.payer_id}`;
    }
  }
  
  // ✅ CORRECCIÓN: Calcular amount basado en el net_amount o unit_price
  const activityNetAmount = activity.amounts?.net || activity.pricing?.unit_price || 0;
  const calculatedAmount = primarySplit.percent 
    ? (activityNetAmount * primarySplit.percent / 100) 
    : (primarySplit.amount || 0);
  
  return {
    name: payerName,
    percentage: primarySplit.percent || 0,
    amount: calculatedAmount, // ✅ Usar amount calculado
    type: payerType
  };
};

const getAllPayersInfo = (activity) => {
  if (!activity.split || !Array.isArray(activity.split) || activity.split.length === 0) {
    return [];
  }
  
  // ✅ Obtener el monto base correcto
  const baseAmount = activity.amounts?.net || activity.pricing?.unit_price || 0;
  
  return activity.split.map((split, index) => {
    let payerName = 'Pagador no identificado';
    let payerType = split.payer_type || 'unknown';
    let companyName = '';
    
    // Obtener nombre del pagador
    if (split.payer_snapshot && split.payer_snapshot.name) {
      payerName = split.payer_snapshot.name;
    } else if (split.payer_id) {
      if (typeof split.payer_id === 'object' && split.payer_id !== null) {
        payerName = getFullName(split.payer_id, 'Pagador no identificado');
      } else {
        payerName = `Pagador ID: ${split.payer_id}`;
      }
    }
    
    // Obtener nombre de la compañía si existe
    if (split.payer_snapshot && split.payer_snapshot.company_name) {
      companyName = split.payer_snapshot.company_name;
    } else if (split.company_id) {
      if (typeof split.company_id === 'object' && split.company_id !== null && split.company_id.name) {
        companyName = split.company_id.name;
      } else if (typeof split.company_id === 'string') {
        companyName = `Compañía ID: ${split.company_id}`;
      }
    }
    
    // ✅ CORRECCIÓN: Calcular monto basado en porcentaje y total correcto
    const percentage = split.percent || 0;
    const calculatedAmount = percentage > 0 && baseAmount > 0 
      ? (baseAmount * percentage / 100) 
      : (split.amount || 0);
    
    return {
      index: index + 1,
      name: payerName,
      companyName: companyName,
      type: payerType,
      percentage: percentage,
      amount: calculatedAmount, // ✅ Usar amount calculado
      originalAmount: split.amount || 0
    };
  }).sort((a, b) => b.percentage - a.percentage);
};

// Función para obtener facturas asociadas - VERSIÓN MEJORADA
const getAssociatedInvoices = (activity) => {
  if (!activity || !activity._original) {
    return [];
  }
  
  // ✅ Si ya hay facturas enriquecidas, usarlas
  if (activity._enriched_invoices) {
    return activity._enriched_invoices;
  }
  
  const originalActivity = activity._original;
  
  if (!originalActivity.so_links || !Array.isArray(originalActivity.so_links) || originalActivity.so_links.length === 0) {
    return [];
  }
  
  return originalActivity.so_links.map((soLink, index) => ({
    index: index + 1,
    sales_order_id: soLink.sales_order_id || 'N/A',
    line_id: soLink.line_id || 'N/A',
    billed_amount: soLink.billed_amount !== undefined ? soLink.billed_amount : 0,
    created_at: soLink.created_at ? formatDate(soLink.created_at) : 'N/A',
    odoo_json: soLink.odoo_json || null,
    payer_info: soLink.payer_info || null,
    status: soLink.status || 'unknown',
    // Info de exportación
    so_status_snapshot: soLink.so_status_snapshot,
    odoo_exported_at: soLink.odoo_exported_at,
    odoo_order_id: soLink.odoo_order_id
  }));
};

// Funciones auxiliares para manejar JSON (iguales que en BillingActionButtonGeneral)
const handleViewJson = (odooJson) => {
  const jsonStr = JSON.stringify(odooJson, null, 2);
  const blob = new Blob([jsonStr], { type: 'application/json' });
  const url = window.URL.createObjectURL(blob);
  window.open(url, '_blank');
  window.URL.revokeObjectURL(url);
};

const handleDownloadJson = (odooJson, invoiceId) => {
  const jsonStr = JSON.stringify(odooJson, null, 2);
  const blob = new Blob([jsonStr], { type: 'application/json' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `invoice_odoo_${invoiceId || Date.now()}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const handleCopyJson = async (odooJson) => {
  try {
    const jsonStr = JSON.stringify(odooJson, null, 2);
    await navigator.clipboard.writeText(jsonStr);
    alert('JSON copiado al portapapeles');
  } catch (error) {
    console.error('Error copiando JSON:', error);
    alert('Error al copiar JSON');
  }
};


  // Función mejorada para transformar datos de actividad
const transformActivityData = (apiData) => {
  console.log('🔍 [transformActivityData] Entrada:', apiData);
  
  if (!apiData || !Array.isArray(apiData)) {
    console.log('❌ [transformActivityData] Datos inválidos:', apiData);
    return [];
  }
  
  return apiData.map((activity, index) => {
    console.log(`🔍 [transformActivityData] Procesando actividad ${index + 1}:`, activity);
      
    // Procesar información del paciente
    const patientInfo = (() => {
      if (!activity.patient_id) {
        return { name: 'Paciente no asignado', id: 'unknown' };
      }
      
      if (typeof activity.patient_id === 'object' && activity.patient_id !== null) {
        return {
          name: getFullName(activity.patient_id, 'Paciente no identificado'),
          id: activity.patient_id._id || 'unknown'
        };
      }
      
      return {
        name: `Paciente ID: ${activity.patient_id}`,
        id: activity.patient_id
      };
    })();

    // Procesar información del médico
    const doctorInfo = (() => {
      if (!activity.doctor_id) {
        return { name: 'Sin médico asignado', id: 'unknown' };
      }
      
      if (typeof activity.doctor_id === 'object' && activity.doctor_id !== null) {
        return {
          name: getFullName(activity.doctor_id, 'Médico no identificado'),
          id: activity.doctor_id._id || 'unknown'
        };
      }
      
      return {
        name: `Médico ID: ${activity.doctor_id}`,
        id: activity.doctor_id
      };
    })();

    // Procesar información del centro
    const centerInfo = (() => {
      if (!activity.center_id) {
        return { name: 'Sin centro asignado', id: 'unknown' };
      }
      
      if (typeof activity.center_id === 'object' && activity.center_id !== null) {
        return {
          name: getSafeValue(activity.center_id.name, 'Centro no identificado'),
          id: activity.center_id._id || 'unknown'
        };
      }
      
      return {
        name: `Centro ID: ${activity.center_id}`,
        id: activity.center_id
      };
    })();

    // ✅ Extraer información de productos - CORREGIDO
    const productInfo = (() => {
      // La estructura correcta viene directamente en activity.product_info
      const products = activity.product_info || {};
      
      console.log('🔍 [productInfo] Datos originales:', products);
      
      // Función auxiliar para extraer precio
      const extractPrice = (priceData) => {
        if (!priceData) return 0;
        if (typeof priceData === 'number') return priceData;
        if (priceData.$numberDecimal) return parseFloat(priceData.$numberDecimal);
        return 0;
      };
      
      const result = {
        // Producto de la cita
        producto_cita_id: products.producto_cita_id || null,
        producto_cita_name: products.producto_cita_name || '',
        producto_cita_code: products.producto_cita_code || '',
        producto_cita_price: extractPrice(products.producto_cita_price),
        
        // Despacho médico
        despacho_medico_id: products.despacho_medico_id || null,
        despacho_medico_name: products.despacho_medico_name || '',
        despacho_medico_code: products.despacho_medico_code || '',
        
        has_product_info: !!(
          products.producto_cita_id || 
          products.despacho_medico_id ||
          products.producto_cita_name ||
          products.despacho_medico_name
        )
      };
      
      console.log('✅ [productInfo] Resultado procesado:', result);
      
      return result;
    })();

    // ✅ CORRECCIÓN: Calcular montos base correctamente
    const unitPrice = activity.pricing?.unit_price || 0;
    const baseAmount = activity.amounts?.net || unitPrice;

    // Obtener información del pagador principal
    const primaryPayer = getPrimaryPayerInfo(activity);

    const transformed = {
      id: activity._id,
      
      // Información del paciente
      patient_name: patientInfo.name,
      patient_id: patientInfo.id,
      
      // Información del médico
      doctor_name: doctorInfo.name,
      doctor_id: doctorInfo.id,
      
      // Información del centro
      center_name: centerInfo.name,
      center_id: centerInfo.id,
      
      // Fecha de realización formateada
      performed_at: formatDate(activity.performed_at),
      performed_at_raw: activity.performed_at, // Mantener original para ordenamiento
      
      // Tipo de actividad
      activity_type: getSafeValue(activity.type, 'other'),
      
      // Descripción
      description: getSafeValue(activity.description, 'Sin descripción'),
      
      // Estado
      status: getSafeValue(activity.status, 'pending'),
      
      // ✅ Montos CORREGIDOS con valores por defecto
      gross_amount: activity.amounts?.gross || unitPrice || 0,
      tax_amount: activity.amounts?.tax || 0,
      net_amount: baseAmount,
      billed_net: activity.amounts?.billed_net || 0,
      remaining_net: activity.amounts?.remaining_net || baseAmount,
      
      // Autorización
      has_authorization: !!(activity.auth_code || activity.authorization_code),
      authorization_code: getSafeValue(activity.auth_code || activity.authorization_code, ''),
      
      // Información del pagador principal
      primary_payer_name: primaryPayer.name,
      primary_payer_percentage: primaryPayer.percentage,
      primary_payer_amount: primaryPayer.amount,
      primary_payer_type: primaryPayer.type,
      
      // ✅ CAMPOS DE PRODUCTOS
      producto_cita_id: productInfo.producto_cita_id,
      producto_cita_name: productInfo.producto_cita_name,
      producto_cita_code: productInfo.producto_cita_code,
      producto_cita_price: productInfo.producto_cita_price,
      
      despacho_medico_id: productInfo.despacho_medico_id,
      despacho_medico_name: productInfo.despacho_medico_name,
      despacho_medico_code: productInfo.despacho_medico_code,
      
      has_product_info: productInfo.has_product_info,

      // Contadores
      split_count: Array.isArray(activity.split) ? activity.split.length : 0,
      so_links_count: Array.isArray(activity.so_links) ? activity.so_links.length : 0,
      
      // Fechas de sistema formateadas
      created_at: formatDate(activity.created_at),
      updated_at: formatDate(activity.updated_at),
      
      // Información adicional para pricing
      currency: activity.pricing?.currency || 'EUR',
      unit_price: unitPrice,
      quantity: activity.pricing?.qty || 1,
      
      // Mantener datos originales para llamadas a la API
      _original: activity
    };
    
    console.log(`✅ [transformActivityData] Actividad ${index + 1} transformada:`, transformed);
    return transformed;
  });
};

  // Update processed activities when activities prop changes
  useEffect(() => {
    console.log('🔍 [BillingTable] Raw activities received:', activities);
    
    const transformed = transformActivityData(activities);
    console.log('🔍 [BillingTable] Transformed activities:', transformed);
    
    setProcessedActivities(transformed);
  }, [activities]);

  // Update row selection when selectedActivities changes
  useEffect(() => {
    const selectionMap = {};
    selectedActivities.forEach(activityId => {
      const index = processedActivities.findIndex(activity => activity.id === activityId);
      if (index !== -1) {
        selectionMap[index] = true;
      }
    });
    setRowSelection(selectionMap);
  }, [selectedActivities, processedActivities]);

  // Load Odoo Sales Order info when detail dialog is opened
  useEffect(() => {
    if (selectedActivityForDetail && showDetailDialog) {
      const loadSalesOrdersInfo = async () => {
        try {
          const invoices = getAssociatedInvoices(selectedActivityForDetail);
          
          if (invoices.length === 0) return;
          
          console.log('🔍 Cargando información de Odoo para facturas...');
          
          // Cargar info de cada Sales Order
          const updatedInvoices = await Promise.all(
            invoices.map(async (invoice) => {
              if (!invoice.sales_order_id) return invoice;
              
              try {
                const response = await fetch(
                  `${API_BASE_URL}/billing/sales-orders/${invoice.sales_order_id}`,
                  {
                    headers: {
                      'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                  }
                );
                
                const result = await response.json();
                
                if (result.data?.order?.odoo) {
                  console.log(`✅ Info Odoo cargada para SO ${invoice.sales_order_id}:`, result.data.order.odoo);
                  return {
                    ...invoice,
                    odoo_info: result.data.order.odoo,
                    sales_order_status: result.data.order.status
                  };
                }
                
                return invoice;
              } catch (error) {
                console.error(`❌ Error cargando SO ${invoice.sales_order_id}:`, error);
                return invoice;
              }
            })
          );
          
          // Actualizar la actividad seleccionada con la info cargada
          setSelectedActivityForDetail(prev => ({
            ...prev,
            _enriched_invoices: updatedInvoices
          }));
          
        } catch (error) {
          console.error('❌ Error general cargando info de Sales Orders:', error);
        }
      };
      
      loadSalesOrdersInfo();
    }
  }, [selectedActivityForDetail, showDetailDialog]);

  // 1. Modificar el useEffect de enriquecimiento para NO perder datos
useEffect(() => {
  const enrichActivitiesWithOdooInfo = async () => {
    if (!processedActivities || processedActivities.length === 0) return;
    
    console.log('🔍 Enriqueciendo actividades con info de Odoo... Trigger:', forceEnrichTrigger);
    
    // ✅ IMPORTANTE: Trabajar con una copia para no perder referencias
    const activitiesToEnrich = processedActivities.filter(activity => {
      // Si el trigger > 0, enriquecer todas
      if (forceEnrichTrigger > 0) return true;
      // Si no, solo las que no están enriquecidas
      return !activity._odoo_enriched;
    });
    
    if (activitiesToEnrich.length === 0) {
      console.log('✅ No hay actividades que enriquecer');
      return;
    }
    
    console.log(`📦 Enriqueciendo ${activitiesToEnrich.length} actividades...`);
    
    // Enriquecer solo las necesarias
    const enrichedUpdates = await Promise.all(
      activitiesToEnrich.map(async (activity) => {
        const soLinks = activity._original?.so_links || [];
        
        if (soLinks.length === 0) {
          return {
            id: activity.id,
            updates: {
              _odoo_enriched: true
            }
          };
        }
        
        // Cargar info de Odoo para cada sales order
        const enrichedSoLinks = await Promise.all(
          soLinks.map(async (soLink) => {
            // Si ya tiene info de Odoo y no forzamos, mantenerla
            if (soLink.odoo?.order_id && forceEnrichTrigger === 0) {
              return soLink;
            }
            
            // Si tiene sales_order_id, intentar cargar
            if (soLink.sales_order_id) {
              try {
                console.log(`🔄 Cargando SO ${soLink.sales_order_id}...`);
                const response = await fetch(
                  `${API_BASE_URL}/billing/sales-orders/${soLink.sales_order_id}`,
                  {
                    headers: {
                      'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                  }
                );
                
                const result = await response.json();
                
                if (result.data?.order?.odoo) {
                  console.log(`✅ Info cargada para SO ${soLink.sales_order_id}:`, result.data.order.odoo);
                  return {
                    ...soLink,
                    odoo: result.data.order.odoo
                  };
                }
              } catch (error) {
                console.error(`❌ Error cargando SO ${soLink.sales_order_id}:`, error);
              }
            }
            
            return soLink;
          })
        );
        
        return {
          id: activity.id,
          updates: {
            _original: {
              ...activity._original,
              so_links: enrichedSoLinks
            },
            _odoo_enriched: true
          }
        };
      })
    );
    
    // ✅ CLAVE: Actualizar solo las actividades modificadas, preservando el resto
    setProcessedActivities(prevActivities => {
      const updatedMap = new Map(enrichedUpdates.map(u => [u.id, u.updates]));
      
      return prevActivities.map(activity => {
        const updates = updatedMap.get(activity.id);
        if (updates) {
          return {
            ...activity,
            ...updates
          };
        }
        return activity;
      });
    });
    
    console.log('✅ Enriquecimiento completado');
  };
  
  // Solo ejecutar si hay actividades procesadas
  if (processedActivities.length > 0) {
    // Si forceEnrichTrigger > 0, siempre enriquecer
    // Si forceEnrichTrigger === 0, solo enriquecer si hay alguna sin enriquecer
    const needsEnrichment = forceEnrichTrigger > 0 || processedActivities.some(activity => !activity._odoo_enriched);
    
    if (needsEnrichment) {
      enrichActivitiesWithOdooInfo();
    }
  }
}, [processedActivities.length, forceEnrichTrigger]); // ✅ Dependencias correctas

// 2. IMPORTANTE: Resetear el trigger después de enriquecer
useEffect(() => {
  if (forceEnrichTrigger > 0) {
    // Resetear el trigger después de 2 segundos
    const timer = setTimeout(() => {
      console.log('🔄 Reseteando trigger de enriquecimiento');
      setForceEnrichTrigger(0);
    }, 2000);
    
    return () => clearTimeout(timer);
  }
}, [forceEnrichTrigger]);


  // Handle row selection changes
  const handleRowSelectionChange = (newSelection) => {
    setRowSelection(newSelection);
    
    // Convert selection to activity IDs
    const selectedIds = Object.keys(newSelection)
      .filter(key => newSelection[key])
      .map(index => processedActivities[parseInt(index)]?.id)
      .filter(Boolean);
    
    onActivitySelection && onActivitySelection(selectedIds);
  };

  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    setActiveFilters(newFilters);
    onFiltersChange && onFiltersChange(newFilters);
  };

  // Handle global search
  const handleGlobalFilterChange = (searchTerm) => {
    setGlobalFilter(searchTerm);
    onFiltersChange && onFiltersChange({ ...filters, search: searchTerm });
  };

  // Handle cell editing
  const handleCellDoubleClick = (cell, row) => {
    if (isEditableField(cell.column.id) && BillingUtils.canEdit(currentUserRoles)) {
      if (editingCell === null) {
        setEditingCell({
          rowId: row.index,
          columnId: cell.column.id,
          value: cell.getValue() || ''
        });
        setEditValue(cell.getValue() || '');
      }
    }
  };

  const isEditableField = (columnId) => {
    const editableFields = [
      'description',
      'authorization_code',
      'notes'
    ];
    return editableFields.includes(columnId);
  };

  const handleSaveEdit = async () => {
    try {
      if (!editingCell) return;
      
      const activity = processedActivities[editingCell.rowId];
      if (!activity) return;
      
      const updateData = {
        [editingCell.columnId]: editValue
      };
      
      await onUpdateActivity(activity.id, updateData);
      
      // Update local state
      setProcessedActivities(prev => {
        const updated = [...prev];
        updated[editingCell.rowId] = {
          ...updated[editingCell.rowId],
          [editingCell.columnId]: editValue
        };
        return updated;
      });
      
      setEditingCell(null);
      setEditValue('');
    } catch (error) {
      console.error('Error updating activity:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  // Text highlighting function mejorada
  const highlightText = (text, fieldName) => {
    if (text === undefined || text === null) return 'N/A';
    
    const textStr = typeof text === 'string' ? text : String(text);
    
    // Si el texto es uno de nuestros fallbacks, no resaltar
    const fallbackTexts = [
      'No especificado', 'No identificado', 'Sin descripción', 
      'Paciente no asignado', 'Sin médico asignado', 'Sin centro asignado',
      'Sin pagador asignado', 'No especificada'
    ];
    
    if (fallbackTexts.includes(textStr)) {
      return <span style={{ fontStyle: 'italic', color: '#666' }}>{textStr}</span>;
    }
    
    let termToHighlight = '';
    if (fieldName && activeFilters && activeFilters[fieldName]) {
      termToHighlight = activeFilters[fieldName].toLowerCase();
    } else if (globalFilter) {
      termToHighlight = globalFilter.toLowerCase();
    }
    
    if (!termToHighlight || !textStr.toLowerCase().includes(termToHighlight)) {
      return textStr;
    }
    
    try {
      const parts = textStr.split(new RegExp(`(${termToHighlight})`, 'gi'));
      return (
        <>
          {parts.map((part, index) => {
            if (!part) return null;
            return part.toLowerCase() === termToHighlight.toLowerCase() ? 
              <span key={index} style={{ backgroundColor: '#FFFF00', fontWeight: 'bold' }}>{part}</span> : 
              part;
          })}
        </>
      );
    } catch (error) {
      console.error("Error highlighting text:", error);
      return textStr;
    }
  };

  // Custom cell renderer mejorado
  const renderCell = (cell, row) => {
    const isEditing = 
      editingCell && 
      editingCell.rowId === row.index && 
      editingCell.columnId === cell.column.id;
    
    if (isEditing) {
      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <TextField
            variant="standard"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            size="small"
            autoFocus
            fullWidth
            onClick={(e) => e.stopPropagation()}
          />
          <IconButton 
            size="small" 
            color="primary" 
            onClick={(e) => {
              e.stopPropagation();
              handleSaveEdit();
            }}
          >
            <SaveIcon fontSize="small" />
          </IconButton>
          <IconButton 
            size="small" 
            color="secondary" 
            onClick={(e) => {
              e.stopPropagation();
              handleCancelEdit();
            }}
          >
            <CancelIcon fontSize="small" />
          </IconButton>
        </div>
      );
    }
    
    return highlightText(cell.getValue(), cell.column.id);
  };

  // Status renderer mejorado
  const renderStatus = (status) => {
    const statusConfig = {
      'billed': { color: 'success', label: 'Facturado', icon: <CheckCircleIcon fontSize="small" /> },
      'draft': { color: 'primary', label: 'Borrador', icon: <InfoIcon fontSize="small" /> },
      'error': { color: 'error', label: 'Error', icon: <ErrorIcon fontSize="small" /> },
      'non_billable': { color: 'secondary', label: 'No Facturable', icon: <WarningIcon fontSize="small" /> },
      'pending': { color: 'warning', label: 'Pendiente', icon: <InfoIcon fontSize="small" /> }
    };

    const config = statusConfig[status?.toLowerCase()] || statusConfig['pending'];

    return (
      <span style={{
      backgroundColor: config.bgColor,
      color: config.color,
      padding: '4px 8px',
      borderRadius: '12px',
      fontSize: '0.75rem',
      fontWeight: 500,
      border: `1px solid ${config.color}`,
      display: 'inline-block'
    }}>
      {config.label}
    </span>
    );
  };

  // Amount renderer with currency formatting mejorado
  const renderAmount = (amount) => {
  const value = Number(amount) || 0;
  return (
    <Box sx={{ 
      fontFamily: 'monospace', 
      textAlign: 'right',
      color: value === 0 ? '#666' : 'inherit',
      fontStyle: value === 0 ? 'italic' : 'normal',
      fontWeight: value > 0 ? 500 : 'normal'
    }}>
      {new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value)}
    </Box>
  );
};

  // Renderizador del pagador principal
const renderPrimaryPayer = (row) => {
  const payer = row.original;
  
  const calculatedAmount = payer.primary_payer_percentage > 0 && payer.net_amount > 0 
    ? (payer.net_amount * payer.primary_payer_percentage / 100).toFixed(2)
    : payer.primary_payer_amount || 0;
  
  return (
    <Box>
      <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5 }}>
        {highlightText(payer.primary_payer_name, 'primary_payer_name')}
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{
          backgroundColor: '#f5f5f5',
          color: '#666',
          padding: '2px 6px',
          borderRadius: '8px',
          fontSize: '0.7rem',
          border: '1px solid #ddd',
          display: 'inline-block'
        }}>
          {payer.primary_payer_type === 'User' ? 'Paciente' : 
           payer.primary_payer_type === 'Company' ? 'Empresa' : 
           payer.primary_payer_type || 'N/A'}
        </span>
        
        {payer.primary_payer_percentage > 0 && (
          <span style={{
            backgroundColor: '#e3f2fd',
            color: '#1976d2',
            padding: '2px 6px',
            borderRadius: '8px',
            fontSize: '0.7rem',
            border: '1px solid #1976d2',
            display: 'inline-block'
          }}>
            {`${payer.primary_payer_percentage}%`}
          </span>
        )}
        
        <Typography variant="caption" color="textSecondary" sx={{ ml: 0.5 }}>
          {BillingUtils.formatCurrency(calculatedAmount)}
        </Typography>
      </Box>
    </Box>
  );
};

  // Handle mark as non-billable
  const handleMarkNonBillableClick = (activityId) => {
    setNonBillableActivityId(activityId);
    setShowNonBillableDialog(true);
  };

  const handleMarkNonBillableSubmit = () => {
    if (nonBillableActivityId && nonBillableReason) {
      onMarkNonBillable && onMarkNonBillable(nonBillableActivityId, nonBillableReason, nonBillableComment);
      setShowNonBillableDialog(false);
      setNonBillableActivityId(null);
      setNonBillableReason('');
      setNonBillableComment('');
    }
  };

  // Handle view detail
  const handleViewDetail = (activity) => {
    setSelectedActivityForDetail(activity);
    setShowDetailDialog(true);
  };

  // Función para obtener el tipo de actividad en español
  const getActivityTypeLabel = (type) => {
    const types = {
      'consultation': 'Consulta',
      'procedure': 'Procedimiento',
      'surgery': 'Cirugía',
      'diagnostic': 'Diagnóstico',
      'therapy': 'Terapia',
      'emergency': 'Emergencia',
      'other': 'Otro'
    };
    return types[type] || 'Otro';
  };

  // Función para obtener el estado en español
  const getStatusLabel = (status) => {
    const statuses = {
      'pending': 'Pendiente',
      'draft': 'Borrador',
      'billed': 'Facturado',
      'error': 'Error',
      'non_billable': 'No Facturable'
    };
    return statuses[status] || 'Desconocido';
  };

  // Column definitions simplificadas
  const columns = useMemo(
    () => [
      {
  id: 'actions',
  header: 'Acciones',
  size: 80,
  enableColumnOrdering: false,
  enableSorting: false,
  Cell: ({ row }) => (
    <Box sx={{ display: 'flex', gap: 0.5 }}>
      <Tooltip title="Ver detalles completos">
        <IconButton 
          size="small" 
          color="info"
          onClick={(e) => {
            e.stopPropagation();
            handleViewDetail(row.original);
          }}
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  ),
},
      {
        accessorKey: 'patient_name',
        header: 'Paciente',
        size: 180,
        Cell: ({ cell, row }) => (
          <Tooltip title={`ID Paciente: ${row.original.patient_id}`}>
            <Box>
              {renderCell(cell, row)}
            </Box>
          </Tooltip>
        ),
      },
      {
        accessorKey: 'performed_at',
        header: 'Fecha',
        size: 110,
        sortingFn: (rowA, rowB) => {
          const dateA = new Date(rowA.original.performed_at_raw || 0);
          const dateB = new Date(rowB.original.performed_at_raw || 0);
          return dateA.getTime() - dateB.getTime();
        },
        Cell: ({ cell, row }) => (
          <Box sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
            {renderCell(cell, row)}
          </Box>
        ),
      },
      {
        accessorKey: 'activity_type',
        header: 'Tipo',
        size: 120,
        Cell: ({ cell }) => (
          <span style={{
      backgroundColor: '#f5f5f5',
      color: '#666',
      padding: '4px 8px',
      borderRadius: '12px',
      fontSize: '0.75rem',
      border: '1px solid #ddd',
      display: 'inline-block'
    }}>
      {getActivityTypeLabel(cell.getValue())}
    </span>
        ),
      },
      {
        accessorKey: 'description',
        header: 'Descripción',
        size: 200,
        Cell: ({ cell, row }) => (
          <Tooltip title={cell.getValue()} placement="top">
            <Box sx={{ 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap',
              maxWidth: '190px'
            }}>
              {renderCell(cell, row)}
            </Box>
          </Tooltip>
        ),
      },
      // Agregar después de la columna 'despacho_medico_name' en la definición de columns

{
  accessorKey: 'odoo_status',
  header: 'Estado Odoo',
  size: 140,
  Cell: ({ row }) => {
    const activity = row.original;
    
    // Verificar si tiene sales orders con info de Odoo
    const soLinks = activity._original?.so_links || [];
    
    if (soLinks.length === 0) {
      return (
        <Typography variant="caption" color="textSecondary" fontStyle="italic">
          Sin facturas
        </Typography>
      );
    }
    
    // Contar cuántas están exportadas a Odoo
    const exportedCount = soLinks.filter(so => 
      so.odoo?.order_id || so.so_status_snapshot === 'exported'
    ).length;
    
    const totalCount = soLinks.length;
    
    if (exportedCount === 0) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <span style={{
            backgroundColor: '#fff3e0',
            color: '#f57c00',
            padding: '4px 8px',
            borderRadius: '12px',
            fontSize: '0.7rem',
            border: '1px solid #f57c00',
            display: 'inline-block'
          }}>
            ⏳ Pendiente
          </span>
          <Typography variant="caption" color="textSecondary">
            ({totalCount})
          </Typography>
        </Box>
      );
    }
    
    if (exportedCount === totalCount) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <span style={{
            backgroundColor: '#e8f5e9',
            color: '#2e7d32',
            padding: '4px 8px',
            borderRadius: '12px',
            fontSize: '0.7rem',
            border: '1px solid #2e7d32',
            display: 'inline-block'
          }}>
            ✅ Exportado
          </span>
          <Typography variant="caption" color="textSecondary">
            ({totalCount})
          </Typography>
        </Box>
      );
    }
    
    // Parcialmente exportado
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <span style={{
          backgroundColor: '#e3f2fd',
          color: '#1976d2',
          padding: '4px 8px',
          borderRadius: '12px',
          fontSize: '0.7rem',
          border: '1px solid #1976d2',
          display: 'inline-block'
        }}>
          📊 Parcial
        </span>
        <Typography variant="caption" color="textSecondary">
          ({exportedCount}/{totalCount})
        </Typography>
      </Box>
    );
  },
},
{
  accessorKey: 'odoo_orders',
  header: 'Órdenes Odoo',
  size: 160,
  Cell: ({ row }) => {
    const activity = row.original;
    const soLinks = activity._original?.so_links || [];
    
    // Intentar extraer info de diferentes ubicaciones
    const odooOrders = soLinks
      .map(so => {
        // Opción 1: Tiene odoo.order_id
        if (so.odoo?.order_id) {
          return {
            orderId: so.odoo.order_id,
            status: so.odoo.status
          };
        }
        
        // Opción 2: Tiene odoo_order_id directamente
        if (so.odoo_order_id) {
          return {
            orderId: so.odoo_order_id,
            status: so.so_status_snapshot || 'unknown'
          };
        }
        
        return null;
      })
      .filter(Boolean);
    
    if (odooOrders.length === 0) {
      return (
        <Typography variant="caption" color="textSecondary" fontStyle="italic">
          -
        </Typography>
      );
    }
    
    // Si solo hay una orden - SIN mostrar CREATED
    if (odooOrders.length === 1) {
      const order = odooOrders[0];
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography 
            variant="body2" 
            sx={{ 
              fontFamily: 'monospace',
              fontWeight: 500,
              color: '#1976d2'
            }}
          >
            SO{order.orderId}
          </Typography>
        </Box>
      );
    }
    
    // Si hay múltiples órdenes
    return (
      <Tooltip 
        title={
          <Box>
            {odooOrders.map((order, idx) => (
              <Box key={idx} sx={{ mb: 0.5 }}>
                SO{order.orderId}
              </Box>
            ))}
          </Box>
        }
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 0.3,
          maxHeight: '60px',
          overflow: 'auto'
        }}>
          {odooOrders.map((order, idx) => (
            <Typography 
              key={idx}
              variant="caption" 
              sx={{ 
                fontFamily: 'monospace',
                fontWeight: 500,
                color: '#1976d2',
                fontSize: '0.7rem'
              }}
            >
              SO{order.orderId}
            </Typography>
          ))}
        </Box>
      </Tooltip>
    );
  },
},
      
      {
        accessorKey: 'remaining_net',
        header: 'Pendiente',
        size: 110,
        Cell: ({ cell }) => (
          <Box sx={{ color: cell.getValue() > 0 ? 'warning.main' : 'success.main' }}>
            {renderAmount(cell.getValue())}
          </Box>
        ),
      },
      {
        id: 'primary_payer_info',
        header: 'Pagador Principal',
        size: 220,
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          const nameA = rowA.original.primary_payer_name || '';
          const nameB = rowB.original.primary_payer_name || '';
          return nameA.localeCompare(nameB);
        },
        Cell: ({ row }) => renderPrimaryPayer(row),
      },
      {
      accessorKey: 'doctor_name',
      header: 'Médico',
      size: 160,
      Cell: ({ cell, row }) => (
        <Tooltip title={`ID Médico: ${row.original.doctor_id}`}>
          <Box sx={{ 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            whiteSpace: 'nowrap',
            maxWidth: '150px'
          }}>
            {renderCell(cell, row)}
          </Box>
        </Tooltip>
      ),
    },
    
    {
      accessorKey: 'despacho_medico_name',
      header: 'Despacho',
      size: 160,
      Cell: ({ row }) => {
        const despachoName = row.original.despacho_medico_name;
        const despachoCode = row.original.despacho_medico_code;
        
        if (!despachoName) {
          return (
            <Typography variant="caption" color="textSecondary" fontStyle="italic">
              Sin despacho
            </Typography>
          );
        }
        
        return (
          <Tooltip title={`Código: ${despachoCode || 'N/A'}`}>
            <Box sx={{ 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap',
              maxWidth: '150px'
            }}>
              <Typography variant="body2">
                {despachoName}
              </Typography>
            </Box>
          </Tooltip>
        );
      },
    },
  ],
  [editingCell, editValue, activeFilters, globalFilter, currentUserRoles, onMarkNonBillable]
);

  return (
    <div >
      {/* Header with filters and KPIs */}
      <BillingKPI 
        activities={processedActivities}
        selectedActivities={selectedActivities}
      />
      
      <BillingFiltersHeader 
      filters={filters}
      onFiltersChange={handleFilterChange}
      onGlobalSearch={handleGlobalFilterChange}
      selectedActivities={selectedActivities}
      activities={activities}
      processedActivities={processedActivities}
      onClearSelection={() => onActivitySelection && onActivitySelection([])}
      onBulkReprice={onBulkReprice}
      onGenerateSalesOrders={onGenerateSalesOrders}
      onReloadData={onReloadData}
      currentUserRoles={currentUserRoles}
      currentCenterId={currentCenterId}
      isProcessingBulkAction={isProcessingBulkAction}
    />

      {/* Main table */}
      <MaterialReactTable
        columns={columns}
        data={processedActivities}
        enableRowSelection
        enableMultiRowSelection
        selectAllMode="all"
        getRowId={(row) => row.id}
        rowSelection={rowSelection}
        onRowSelectionChange={handleRowSelectionChange}
        
        // Loading state
        state={{
          isLoading: isLoading,
          showAlertBanner: false,
          showProgressBars: false,
          rowSelection: rowSelection,
          globalFilter: globalFilter,
          pagination: {
            pageIndex: pagination?.page || 0,
            pageSize: pagination?.limit || 10,
          },
        }}
        
        // Pagination
        manualPagination={true}
        rowCount={pagination?.total || processedActivities.length}
        onPaginationChange={(updater) => {
          const newPagination = typeof updater === 'function' 
            ? updater({ pageIndex: pagination?.page || 0, pageSize: pagination?.limit || 10 })
            : updater;
          onPageChange && onPageChange(newPagination.pageIndex, newPagination.pageSize);
        }}
        
        // Filtering
        enableGlobalFilter
        onGlobalFilterChange={setGlobalFilter}
        globalFilterFn="contains"
        
        // Sorting
        enableSorting
        enableMultiSort
        
        // Styling
        muiTableProps={{
          sx: {
            tableLayout: 'fixed',
            '& .MuiTableCell-root': {
              borderBottom: '1px solid rgba(224, 224, 224, 1)',
            },
          },
        }}
        
        muiTableHeadCellProps={{
          sx: {
            fontWeight: 600,
            backgroundColor: '#ffffffff',
            borderBottom: '2px solid rgba(224, 224, 224, 1)',
          },
        }}
        
        muiTableBodyRowProps={({ row }) => ({
  
  sx: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
    ...(selectedActivities.includes(row.original.id) && {
      backgroundColor: 'rgba(25, 118, 210, 0.08)',
    }),
  },
        })}
        
        muiTableBodyCellProps={({ cell, row }) => ({
          onDoubleClick: () => handleCellDoubleClick(cell, row),
          sx: {
            padding: '8px 12px',
            fontSize: '0.875rem',
          },
        })}
        
        // Toolbar customization
        enableToolbarInternalActions={true}
enableColumnFilters={true}
enableColumnOrdering={true}
enableDensityToggle={true}
enableFullScreenToggle={true}
enableHiding={true}
        renderTopToolbarCustomActions={() => (
  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
    {selectedActivities.length > 0 && (
      <span style={{
        backgroundColor: '#e3f2fd',
        color: '#1976d2',
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '0.75rem',
        border: '1px solid #1976d2',
        display: 'inline-block'
      }}>
        {`${selectedActivities.length} seleccionada${selectedActivities.length !== 1 ? 's' : ''}`}
      </span>
    )}
  </Box>
)}
        
        // Localization
        localization={{
  noRecordsToDisplay: 'No hay actividades para mostrar',
  noResultsFound: 'No se encontraron resultados',
  rowsPerPage: 'Filas por página:',
  showHideColumns: 'Columnas',
  showHideFilters: 'Filtros',
  showHideSearch: 'Búsqueda',
  toggleSelectAll: 'Seleccionar todo',
  toggleSelectRow: 'Seleccionar fila',
  sortByColumnAsc: 'Ordenar ascendente',
  sortByColumnDesc: 'Ordenar descendente',
  clearSort: 'Limpiar orden',
  toggleDensity: 'Densidad',
  toggleFullScreen: 'Pantalla completa',
  actions: 'Acciones',
  and: 'y',
  cancel: 'Cancelar',
  changeFilterMode: 'Cambiar modo de filtro',
  changeSearchMode: 'Cambiar modo de búsqueda',
  clearFilter: 'Limpiar filtro',
  clearSearch: 'Limpiar búsqueda',
  clearSort: 'Limpiar ordenación',
  clickToCopy: 'Clic para copiar',
  collapse: 'Colapsar',
  collapseAll: 'Colapsar todo',
  columnActions: 'Acciones de columna',
  copiedToClipboard: 'Copiado al portapapeles',
  dropToGroupBy: 'Soltar para agrupar por {column}',
  edit: 'Editar',
  expand: 'Expandir',
  expandAll: 'Expandir todo',
  filterArrIncludes: 'Incluye',
  filterArrIncludesAll: 'Incluye todos',
  filterArrIncludesSome: 'Incluye algunos',
  filterBetween: 'Entre',
  filterBetweenInclusive: 'Entre (inclusivo)',
  filterByColumn: 'Filtrar por {column}',
  filterContains: 'Contiene',
  filterEmpty: 'Vacío',
  filterEndsWith: 'Termina con',
  filterEquals: 'Igual a',
  filterEqualsDate: 'Igual a',
  filterEqualsNumber: 'Igual a',
  filterFuzzy: 'Búsqueda difusa',
  filterGreaterThan: 'Mayor que',
  filterGreaterThanOrEqualTo: 'Mayor o igual que',
  filterInNumberRange: 'Entre',
  filterIncludesString: 'Contiene',
  filterIncludesStringSensitive: 'Contiene (sensible a mayúsculas)',
  filterLessThan: 'Menor que',
  filterLessThanOrEqualTo: 'Menor o igual que',
  filterMode: 'Modo de filtro: {filterType}',
  filterNotEmpty: 'No vacío',
  filterNotEquals: 'No igual a',
  filterStartsWith: 'Comienza con',
  filterWeakEquals: 'Igual a',
  filteringByColumn: 'Filtrando por {column} - {filterType}: {filterValue}',
  goToFirstPage: 'Ir a la primera página',
  goToLastPage: 'Ir a la última página',
  goToNextPage: 'Ir a la página siguiente',
  goToPreviousPage: 'Ir a la página anterior',
  grab: 'Agarrar',
  groupByColumn: 'Agrupar por {column}',
  groupedBy: 'Agrupado por ',
  hideAll: 'Ocultar todo',
  hideColumn: 'Ocultar columna {column}',
  max: 'Máximo',
  min: 'Mínimo',
  move: 'Mover',
  pinToLeft: 'Fijar a la izquierda',
  pinToRight: 'Fijar a la derecha',
  resetColumnSize: 'Restablecer tamaño de columna',
  resetOrder: 'Restablecer orden',
  rowActions: 'Acciones de fila',
  rowNumber: '#',
  rowNumbers: 'Números de fila',
  rowsPerPage: 'Filas por página',
  save: 'Guardar',
  search: 'Buscar',
  select: 'Seleccionar',
  selectedCountOfRowCountRowsSelected: '{selectedCount} de {rowCount} filas seleccionadas',
  showAll: 'Mostrar todo',
  showAllColumns: 'Mostrar todas las columnas',
  showHideColumns: 'Mostrar/Ocultar columnas',
  showHideFilters: 'Mostrar/Ocultar filtros',
  showHideSearch: 'Mostrar/Ocultar búsqueda',
  sortByColumnAsc: 'Ordenar por {column} ascendente',
  sortByColumnDesc: 'Ordenar por {column} descendente',
  sortedByColumnAsc: 'Ordenado por {column} ascendente',
  sortedByColumnDesc: 'Ordenado por {column} descendente',
  thenBy: ', luego por ',
  toggleDensity: 'Alternar densidad',
  toggleFullScreen: 'Alternar pantalla completa',
  toggleSelectAll: 'Alternar seleccionar todo',
  toggleSelectRow: 'Alternar seleccionar fila',
  toggleVisibility: 'Alternar visibilidad',
  ungroupByColumn: 'Desagrupar por {column}',
  unpin: 'Desprender',
  unpinAll: 'Desprender todo',
  unsorted: 'Sin ordenar',
}}
      />

      {/* Dialog for marking as non-billable */}
      <Dialog 
        open={showNonBillableDialog} 
        onClose={() => setShowNonBillableDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Marcar como No Facturable</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Razón</InputLabel>
              <Select
                value={nonBillableReason}
                onChange={(e) => setNonBillableReason(e.target.value)}
                label="Razón"
              >
                <MenuItem value="no_authorization">Sin autorización</MenuItem>
                <MenuItem value="insurance_rejection">Rechazo del seguro</MenuItem>
                <MenuItem value="patient_cancellation">Cancelación del paciente</MenuItem>
                <MenuItem value="medical_reasons">Razones médicas</MenuItem>
                <MenuItem value="administrative_error">Error administrativo</MenuItem>
                <MenuItem value="other">Otro</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Comentario (opcional)"
              value={nonBillableComment}
              onChange={(e) => setNonBillableComment(e.target.value)}
              multiline
              rows={3}
              variant="outlined"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNonBillableDialog(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleMarkNonBillableSubmit}
            variant="contained"
            disabled={!nonBillableReason}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for activity detail view */}
      <Dialog
  open={showDetailDialog}
  onClose={() => setShowDetailDialog(false)}
  maxWidth="lg"
  fullWidth
>
  <DialogTitle sx={{ pb: 2, borderBottom: '1px solid #e0e0e0' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <InfoIcon color="primary" />
      <Typography variant="h6">
        Detalles Completos de la Actividad
      </Typography>
    </Box>
  </DialogTitle>
  
  <DialogContent sx={{ mt: 2 }}>
    {selectedActivityForDetail && (
      <Grid container spacing={3}>
        
        {/* Información General de la Actividad */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, mb: 2, backgroundColor: '#f8f9fa' }}>
            <Typography variant="h6" gutterBottom color="primary">
              📋 Información General
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="textSecondary">ID de Actividad</Typography>
                <Typography variant="body1" fontWeight="medium">{selectedActivityForDetail.id}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="textSecondary">Fecha de Realización</Typography>
                <Typography variant="body1" fontWeight="medium">{selectedActivityForDetail.performed_at}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="textSecondary">Tipo de Actividad</Typography>
                <span style={{
  backgroundColor: '#e3f2fd',
  color: '#1976d2',
  padding: '4px 8px',
  borderRadius: '12px',
  fontSize: '0.75rem',
  border: '1px solid #1976d2',
  display: 'inline-block'
}}>{getActivityTypeLabel(selectedActivityForDetail.activity_type)}</span>
                
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="textSecondary">Estado</Typography>
                {renderStatus(selectedActivityForDetail.status)}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">Descripción</Typography>
                <Typography variant="body1">{selectedActivityForDetail.description}</Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Información del Paciente, Médico y Centro */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PersonIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Paciente</Typography>
            </Box>
            <Typography variant="subtitle2" color="textSecondary">Nombre</Typography>
            <Typography variant="body1" fontWeight="medium" gutterBottom>{selectedActivityForDetail.patient_name}</Typography>
            <Typography variant="subtitle2" color="textSecondary">ID</Typography>
            <Typography variant="body2" color="textSecondary">{selectedActivityForDetail.patient_id}</Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LocalHospitalIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Médico</Typography>
            </Box>
            <Typography variant="subtitle2" color="textSecondary">Nombre</Typography>
            <Typography variant="body1" fontWeight="medium" gutterBottom>{selectedActivityForDetail.doctor_name}</Typography>
            <Typography variant="subtitle2" color="textSecondary">ID</Typography>
            <Typography variant="body2" color="textSecondary">{selectedActivityForDetail.doctor_id}</Typography>
          </Paper>
        </Grid>

        {/* ✅ SECCIÓN MEJORADA: Información de Productos/Servicios */}
{selectedActivityForDetail.has_product_info && (
  <Grid item xs={12}>
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <ReceiptIcon color="primary" sx={{ mr: 1 }} />
        <Typography variant="h6">Productos/Servicios Asociados</Typography>
      </Box>
      
      <Grid container spacing={3}>
        {/* Producto de la Cita */}
        {selectedActivityForDetail.producto_cita_name && (
          <>
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary">
                📦 Producto/Servicio de la Cita
              </Typography>
              <Divider sx={{ my: 1 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">Nombre</Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedActivityForDetail.producto_cita_name}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="subtitle2" color="textSecondary">Código</Typography>
              <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                {selectedActivityForDetail.producto_cita_code || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="subtitle2" color="textSecondary">Precio Base</Typography>
              <Typography variant="body1" fontWeight="bold" color="primary">
                {new Intl.NumberFormat('es-ES', {
                  style: 'currency',
                  currency: 'EUR',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                }).format(selectedActivityForDetail.producto_cita_price || 0)}
              </Typography>
            </Grid>
          </>
        )}

        {/* Despacho Médico */}
        {selectedActivityForDetail.despacho_medico_name && (
          <>
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary">
                🏥 Despacho Médico
              </Typography>
              <Divider sx={{ my: 1 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">Nombre del Despacho</Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedActivityForDetail.despacho_medico_name}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">Código del Despacho</Typography>
              <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                {selectedActivityForDetail.despacho_medico_code || 'N/A'}
              </Typography>
            </Grid>
          </>
        )}

        {/* Si no hay productos ni despacho */}
        {!selectedActivityForDetail.producto_cita_name && !selectedActivityForDetail.despacho_medico_name && (
          <Grid item xs={12}>
            <Alert severity="info">
              No hay información de productos/servicios ni despachos médicos disponible para esta actividad.
            </Alert>
          </Grid>
        )}
      </Grid>
    </Paper>
  </Grid>
)}


        {/* Información Financiera */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <AttachMoneyIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Información Financiera</Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><Typography variant="subtitle2">Concepto</Typography></TableCell>
                    <TableCell align="right"><Typography variant="subtitle2">Monto</Typography></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
  <TableRow>
    <TableCell><strong>Precio Unitario</strong></TableCell>
    <TableCell align="right">
      <Box sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
        {new Intl.NumberFormat('es-ES', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(selectedActivityForDetail.unit_price || 0)}
      </Box>
    </TableCell>
  </TableRow>
  <TableRow>
    <TableCell><strong>Cantidad</strong></TableCell>
    <TableCell align="right">{selectedActivityForDetail.quantity}</TableCell>
  </TableRow>
  <TableRow>
    <TableCell><strong>Monto Neto</strong></TableCell>
    <TableCell align="right">
      <Box sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
        {new Intl.NumberFormat('es-ES', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(selectedActivityForDetail.net_amount || 0)}
      </Box>
    </TableCell>
  </TableRow>
  <TableRow>
    <TableCell><strong>Impuestos</strong></TableCell>
    <TableCell align="right">
      <Box sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
        {new Intl.NumberFormat('es-ES', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(selectedActivityForDetail.tax_amount || 0)}
      </Box>
    </TableCell>
  </TableRow>
  <TableRow>
    <TableCell><strong>Monto Bruto</strong></TableCell>
    <TableCell align="right">
      <Box sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
        {new Intl.NumberFormat('es-ES', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(selectedActivityForDetail.gross_amount || 0)}
      </Box>
    </TableCell>
  </TableRow>
  <TableRow>
    <TableCell>Ya Facturado</TableCell>
    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
      {new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(selectedActivityForDetail.billed_net || 0)}
    </TableCell>
  </TableRow>
  <TableRow sx={{ backgroundColor: selectedActivityForDetail.remaining_net > 0 ? '#fff3e0' : '#e8f5e8' }}>
    <TableCell><strong>Pendiente por Facturar</strong></TableCell>
    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
      <strong style={{ color: selectedActivityForDetail.remaining_net > 0 ? '#f57c00' : '#2e7d32' }}>
        {new Intl.NumberFormat('es-ES', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(selectedActivityForDetail.remaining_net || 0)}
      </strong>
    </TableCell>
  </TableRow>
</TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Información de Pagadores */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <ReceiptIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Distribución de Pagadores</Typography>
            </Box>
            
            {(() => {
              const payers = getAllPayersInfo(selectedActivityForDetail._original);
              
              if (payers.length === 0) {
                return (
                  <Alert severity="info">
                    No hay información de pagadores disponible para esta actividad.
                  </Alert>
                );
              }
              
              return (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><Typography variant="subtitle2">#</Typography></TableCell>
                        <TableCell><Typography variant="subtitle2">Pagador</Typography></TableCell>                        
                        <TableCell><Typography variant="subtitle2">Tipo</Typography></TableCell>
                        <TableCell align="center"><Typography variant="subtitle2">%</Typography></TableCell>
                        <TableCell align="right"><Typography variant="subtitle2">Monto Calculado</Typography></TableCell>
                        <TableCell align="right"><Typography variant="subtitle2">Monto Original</Typography></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payers.map((payer, index) => (
                        <TableRow key={index} sx={{ backgroundColor: index === 0 ? '#e3f2fd' : 'inherit' }}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={index === 0 ? 'bold' : 'normal'}>
                              {payer.index}
                              {index === 0 && ' (Principal)'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={index === 0 ? 'medium' : 'normal'}>
                              {payer.name}
                            </Typography>
                          </TableCell>                          
                          <TableCell>
                            <span style={{
  backgroundColor: '#e3f2fd',
  color: '#1976d2',
  padding: '4px 8px',
  borderRadius: '12px',
  fontSize: '0.75rem',
  border: '1px solid #1976d2',
  display: 'inline-block'
}}>{
                                payer.type === 'User' ? 'Paciente' : 
                                payer.type === 'Company' ? 'Empresa' : 
                                payer.type || 'N/A'
                              }</span>
                            
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight={index === 0 ? 'bold' : 'normal'}>
                              {payer.percentage}%
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
  <Typography variant="body2" fontWeight={index === 0 ? 'bold' : 'normal'}>
    {new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(payer.amount || 0)}
  </Typography>
</TableCell>
<TableCell align="right" sx={{ fontFamily: 'monospace' }}>
  <Typography variant="body2" color="textSecondary">
    {new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(payer.originalAmount || 0)}
  </Typography>
</TableCell>
                        </TableRow>
                      ))}
                      
                      {/* Fila de totales */}
                      <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                        <TableCell colSpan={3}>
                          <Typography variant="body2" fontWeight="bold">
                            TOTAL
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="bold">
                            {payers.reduce((sum, p) => sum + p.percentage, 0)}%
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
  <Typography variant="body2" fontWeight="bold">
    {new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(payers.reduce((sum, p) => sum + p.amount, 0))}
  </Typography>
</TableCell>
<TableCell align="right" sx={{ fontFamily: 'monospace' }}>
  <Typography variant="body2" fontWeight="bold">
    {new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(payers.reduce((sum, p) => sum + p.originalAmount, 0))}
  </Typography>
</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              );
            })()}
          </Paper>
        </Grid>

        {/* Información de Autorización */}
        {selectedActivityForDetail.authorization_code && (
          <Grid item xs={12}>
            <Alert severity="success" icon={<CheckCircleIcon />}>
              <Typography variant="subtitle2">
                Código de Autorización: <strong>{selectedActivityForDetail.authorization_code}</strong>
              </Typography>
            </Alert>
          </Grid>
        )}

{/* Facturas Asociadas - CON BOTÓN PARA GENERAR EN ODOO Y JSON */}
<Grid item xs={12}>
  <Paper sx={{ p: 3 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      <ReceiptIcon color="primary" sx={{ mr: 1 }} />
      <Typography variant="h6">Facturas Asociadas (Sales Orders)</Typography>
    </Box>
    
    {(() => {
      const invoices = getAssociatedInvoices(selectedActivityForDetail);
      
      if (invoices.length === 0) {
        return (
          <Alert severity="info">
            Esta actividad aún no tiene facturas asociadas.
          </Alert>
        );
      }
      
      return (
        <>
          <Alert severity="success" sx={{ mb: 2 }}>
            Se encontraron <strong>{invoices.length}</strong> factura(s) asociada(s) a esta actividad.
          </Alert>
          
          {invoices.map((invoice, index) => {
            // Información de Odoo (cargada por useEffect)
            const hasOdooInfo = invoice.odoo_info;
            
            return (
              <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                <CardHeader
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="h6">
                        Factura #{invoice.sales_order_id}
                      </Typography>
                      
                      {/* Badges de estado */}
                      {hasOdooInfo ? (
                        <>
                          <span style={{
                            backgroundColor: '#4caf50',
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: 500
                          }}>
                            ✅ Exportada → SO{invoice.odoo_info.order_id}
                          </span>
                          
                          <span style={{
                            backgroundColor: '#2196f3',
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: 500
                          }}>
                            📋 {invoice.odoo_info.status.toUpperCase()}
                          </span>
                        </>
                      ) : (
                        <span style={{
                          backgroundColor: '#ff9800',
                          color: 'white',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          fontWeight: 500
                        }}>
                          ⏳ Pendiente
                        </span>
                      )}
                    </Box>
                  }
                  subheader={`Creada: ${invoice.created_at}`}
                  sx={{ 
                    backgroundColor: '#f5f5f5',
                    borderBottom: '1px solid #e0e0e0'
                  }}
                />
                <CardContent>
                  <Grid container spacing={2}>
                    
                    {/* Información de Odoo */}
                    {hasOdooInfo && (
                      <Grid item xs={12}>
                        <Paper 
                          elevation={0} 
                          sx={{ 
                            p: 2, 
                            backgroundColor: '#e8f5e9',
                            border: '1px solid #4caf50',
                            borderRadius: 2
                          }}
                        >
                          <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="success.main">
                            🔗 Información de Odoo
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="caption" color="textSecondary">
                                Order ID en Odoo
                              </Typography>
                              <Typography variant="body1" fontWeight="bold">
                                SO{invoice.odoo_info.order_id}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="caption" color="textSecondary">
                                Estado
                              </Typography>
                              <Typography variant="body1" fontWeight="medium">
                                {invoice.odoo_info.status || 'N/A'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="caption" color="textSecondary">
                                Exportado el
                              </Typography>
                              <Typography variant="body2">
                                {new Date(invoice.odoo_info.exported_at).toLocaleString('es-ES')}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Paper>
                      </Grid>
                    )}
                    
                    {/* 🔥 BOTÓN PARA GENERAR EN ODOO (solo si NO está exportada) */}
                    {!hasOdooInfo && (
                      <Grid item xs={12}>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          <Typography variant="body2" gutterBottom>
                            Esta factura aún no ha sido generada en Odoo.
                          </Typography>
                          <Button
  variant="contained"
  color="primary"
  size="small"
  onClick={async () => {
    if (!window.confirm('¿Generar esta factura en Odoo?')) {
      return;
    }
    
    try {
      setIsCreating(true);
      
      const response = await fetch(`${API_BASE_URL}/billing/export-to-odoo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          sales_order_ids: [invoice.sales_order_id]
        })
      });
      
      const result = await response.json();
      
      if (result.error) {
        throw new Error(result.error.message);
      }
      
      alert('✅ Factura generada exitosamente en Odoo');
      
      // ✅ Cerrar el dialog
      setShowDetailDialog(false);
      
      // ✅ SOLUCIÓN: Incrementar el trigger para forzar re-enriquecimiento
      setForceEnrichTrigger(prev => prev + 1);
      
      // ✅ Esperar un momento y recargar datos desde el servidor
      setTimeout(() => {
        if (onReloadData) {
          onReloadData();
        }
      }, 500);
      
    } catch (error) {
      console.error('❌ Error generando factura:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setIsCreating(false);
    }
  }}
  startIcon={<FaFileInvoice />}
  disabled={isCreating}
  sx={{ mt: 1 }}
>
  {isCreating ? 'Generando...' : 'Generar Factura en Odoo'}
</Button>
                        </Alert>
                      </Grid>
                    )}                   
                    
                    
                    {/* Información del pagador */}
                    {invoice.payer_info && (
                      <>
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                            Información del Pagador
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" color="textSecondary">
                            Pagador
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {invoice.payer_info.name || 'No especificado'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" color="textSecondary">
                            Tipo
                          </Typography>
                          <Typography variant="body1">
                            {invoice.payer_info.type_description || 'N/A'}
                          </Typography>
                        </Grid>
                      </>
                    )}
                    
                    {/* 🔥 JSON PARA ODOO CON BOTONES */}
                    {invoice.odoo_json && (
                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          JSON para Odoo:
                        </Typography>
                        <Box sx={{ 
                          bgcolor: '#f5f5f5', 
                          p: 2, 
                          borderRadius: 1, 
                          maxHeight: 200, 
                          overflow: 'auto',
                          fontFamily: 'monospace',
                          fontSize: '0.85rem',
                          border: '1px solid #e0e0e0'
                        }}>
                          <pre>{JSON.stringify(invoice.odoo_json, null, 2)}</pre>
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2, flexWrap: 'wrap' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleViewJson(invoice.odoo_json)}
                            startIcon={<VisibilityIcon />}
                          >
                            Ver JSON
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            onClick={() => handleDownloadJson(invoice.odoo_json, invoice.sales_order_id)}
                            startIcon={<SaveIcon />}
                          >
                            Descargar JSON
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            onClick={() => handleCopyJson(invoice.odoo_json)}
                            startIcon={<InfoIcon />}
                          >
                            Copiar JSON
                          </Button>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            );
          })}
        </>
      );
    })()}
  </Paper>
</Grid>

{/* Información Adicional y Contadores */}
<Grid item xs={12}>
  <Paper sx={{ p: 2 }}>
    <Typography variant="subtitle1" gutterBottom>Información Adicional</Typography>
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
      <span style={{
        backgroundColor: '#e3f2fd',
        color: '#1976d2',
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '0.75rem',
        border: '1px solid #1976d2',
        display: 'inline-block'
      }}>{`${selectedActivityForDetail.split_count} División${selectedActivityForDetail.split_count !== 1 ? 'es' : ''} de Pago`}</span>
      <span style={{
        backgroundColor: '#e3f2fd',
        color: '#1976d2',
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '0.75rem',
        border: '1px solid #1976d2',
        display: 'inline-block'
      }}>{`${selectedActivityForDetail.so_links_count} Factura${selectedActivityForDetail.so_links_count !== 1 ? 's' : ''} Asociada${selectedActivityForDetail.so_links_count !== 1 ? 's' : ''}`}</span>
      <span style={{
        backgroundColor: '#e3f2fd',
        color: '#1976d2',
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '0.75rem',
        border: '1px solid #1976d2',
        display: 'inline-block'
      }}>{`Moneda: ${selectedActivityForDetail.currency}`}</span>
      <span style={{
        backgroundColor: '#e3f2fd',
        color: '#1976d2',
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '0.75rem',
        border: '1px solid #1976d2',
        display: 'inline-block'
      }}>{`Cantidad: ${selectedActivityForDetail.quantity}`}</span>
      <span style={{
        backgroundColor: '#e3f2fd',
        color: '#1976d2',
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '0.75rem',
        border: '1px solid #1976d2',
        display: 'inline-block'
      }}>{`Precio Unitario: ${BillingUtils.formatCurrency(selectedActivityForDetail.unit_price)}`}</span>
    </Box>
    
    <Typography variant="body2" color="textSecondary">
      <strong>Creado:</strong> {selectedActivityForDetail.created_at} | 
      <strong> Actualizado:</strong> {selectedActivityForDetail.updated_at}
    </Typography>
  </Paper>
</Grid>

      </Grid>
    )}
  </DialogContent>
  
  <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
    <Button 
      onClick={() => setShowDetailDialog(false)}
      variant="contained"
      color="primary"
      size="large"
    >
      Cerrar
    </Button>
  </DialogActions>
</Dialog>
    </div>
  );
};

export default BillingTable;